/**
 * Idle scheduling for SPECULATIVE work.
 *
 * Speculative prefetch (viewport-triggered data, sibling-tab priming) must
 * never compete with the navigation that is actually on screen: a burst of
 * background requests on a phone connection delays the foreground fetch, and
 * the effect is the opposite of the one intended. Everything here therefore
 * runs only when the browser reports idle time, with a hard timeout so it
 * still happens eventually on a permanently busy page.
 */

type IdleWindow = Window & {
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  cancelIdleCallback?: (handle: number) => void;
};

/**
 * Run `fn` when the main thread is idle (or after `fallbackMs` on browsers
 * without requestIdleCallback). Returns a cancel function.
 */
export function whenIdle(fn: () => void, timeout = 2500, fallbackMs = 1200): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const w = window as IdleWindow;
  if (w.requestIdleCallback) {
    const id = w.requestIdleCallback(fn, { timeout });
    return () => w.cancelIdleCallback?.(id);
  }
  const timer = window.setTimeout(fn, fallbackMs);
  return () => window.clearTimeout(timer);
}

/**
 * A single global FIFO drained while idle, one task at a time.
 *
 * Scheduling each speculative fetch separately is not enough: four components
 * each calling requestIdleCallback all fire in the same idle window, so the
 * requests still go out together. Routing every speculative fetch through
 * this queue guarantees at most ONE is in flight, leaving the rest of the
 * connection for whatever the user is actually doing.
 */
const queue: Array<() => Promise<unknown> | unknown> = [];
let draining = false;

export function enqueueIdleTask(task: () => Promise<unknown> | unknown): void {
  queue.push(task);
  if (draining) return;
  draining = true;
  whenIdle(() => {
    void (async () => {
      try {
        while (queue.length > 0) {
          const next = queue.shift();
          if (!next) break;
          try {
            await next();
          } catch {
            // speculative work is best-effort
          }
        }
      } finally {
        draining = false;
      }
    })();
  });
}

/**
 * Prime a list of sibling queries ONE AT A TIME while idle. Sequential (not
 * parallel) on purpose: each page of a sibling list is a full round trip, and
 * firing four at once starves whichever navigation the user performs next.
 * Every step is best-effort — a failed prime simply means the tab fetches on
 * first use, exactly as it did before priming existed.
 */
export function primeSiblingsWhenIdle(primes: Array<() => Promise<unknown>>): void {
  // Enqueue in priority order; the shared drainer runs them one at a time.
  for (const prime of primes) enqueueIdleTask(prime);
}
